import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { hasPerm, useAuth } from "@/lib/auth";
import { T } from "@/constants/testIds";

const STATUSES = ["draft", "in_review", "theological_review", "approved", "scheduled", "published"];

const EMPTY = {
  slug: "",
  title: "",
  summary: "",
  body: "",
  category: "articles",
  language: "en",
  tags: "",
  primary_scripture: "",
  supporting_scriptures: "",
  context_note: "",
  does_not_mean: "",
  application: "",
  reflection_questions: "",
  seo_title: "",
  seo_description: "",
  reading_time_min: 5,
  featured: false,
  status: "draft",
};

export default function CMSArticleEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/cms/articles/${id}`)
      .then((r) => {
        const d = r.data;
        setForm({
          ...EMPTY,
          ...Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v ?? EMPTY[k] ?? ""])),
          tags: (d.tags || []).join(", "),
          supporting_scriptures: (d.supporting_scriptures || []).join("; "),
          reflection_questions: (d.reflection_questions || []).join("\n"),
        });
      })
      .catch(() => toast.error("Article not found"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const payload = () => ({
    slug: form.slug.trim(),
    title: form.title,
    summary: form.summary,
    body: form.body,
    category: form.category,
    language: form.language,
    tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
    primary_scripture: form.primary_scripture || null,
    supporting_scriptures: form.supporting_scriptures.split(";").map((s) => s.trim()).filter(Boolean),
    context_note: form.context_note || null,
    does_not_mean: form.does_not_mean || null,
    application: form.application || null,
    reflection_questions: form.reflection_questions.split("\n").map((s) => s.trim()).filter(Boolean),
    seo_title: form.seo_title || null,
    seo_description: form.seo_description || null,
    reading_time_min: parseInt(form.reading_time_min) || 5,
    featured: !!form.featured,
  });

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/cms/articles", payload());
        toast.success("Draft created");
        nav(`/cms/articles/${data.id}`);
      } else {
        await api.patch(`/cms/articles/${id}`, payload());
        toast.success("Saved");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/cms/articles/${id}`, { status });
      toast.success(`Status → ${status}`);
      update("status", status);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-ink-muted">Loading…</p>;

  const canPublish = hasPerm(user, "publish");
  const canReview = hasPerm(user, "review");
  const allowedStatuses = STATUSES.filter((s) => {
    if (s === "published" || s === "scheduled") return canPublish;
    if (s === "approved") return canReview;
    return true;
  });

  return (
    <div data-testid={T.cmsArticleEditor}>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <Link to="/cms/articles" className="text-xs uppercase tracking-widest text-ink-muted hover:text-forest">
            ← Articles
          </Link>
          <h1 className="heading-2 mt-2">{isNew ? "New Article" : "Edit Article"}</h1>
          {!isNew && form.author_name && (
            <p className="text-sm text-ink-muted mt-1" data-testid="editor-author-byline">
              By <span className="text-ink font-medium">{form.author_name}</span>
              {form.reviewer_name ? <span> · Reviewed by {form.reviewer_name}</span> : null}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!isNew && (
            <select
              data-testid={T.cmsStatusSelect}
              value={form.status}
              onChange={(e) => changeStatus(e.target.value)}
              className="border border-border bg-paper px-3 py-2 text-sm"
            >
              {allowedStatuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
              {!allowedStatuses.includes(form.status) && <option value={form.status}>{form.status}</option>}
            </select>
          )}
          <button data-testid={T.cmsSaveBtn} onClick={save} disabled={busy} className="btn-primary">
            {busy ? "Saving…" : isNew ? "Create Draft" : "Save"}
          </button>
          {!isNew && !canPublish && form.status === "draft" && (
            <button data-testid="cms-submit-review-btn" onClick={() => changeStatus("in_review")} className="btn-gold">
              Submit for Review
            </button>
          )}
          {!isNew && canReview && ["in_review", "theological_review"].includes(form.status) && (
            <button data-testid="cms-approve-btn" onClick={() => changeStatus("approved")} className="btn-ghost">
              Approve
            </button>
          )}
          {!isNew && canPublish && form.status !== "published" && (
            <button data-testid={T.cmsPublishBtn} onClick={() => changeStatus("published")} className="btn-gold">
              Publish
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          <Field label="Title" testid="title">
            <input value={form.title} onChange={(e) => update("title", e.target.value)} className="fld" />
          </Field>
          <Field label="Slug" testid="slug" hint="URL-safe, lower-case-dashed. Cannot be changed after publish.">
            <input value={form.slug} onChange={(e) => update("slug", e.target.value)} className="fld font-mono" />
          </Field>
          <Field label="Summary" testid="summary" hint="A short plain-language summary shown on cards and article headers.">
            <textarea rows={3} value={form.summary} onChange={(e) => update("summary", e.target.value)} className="fld" />
          </Field>
          <Field label="Body (HTML)" testid="body" hint="You may use headings (h2, h3), paragraphs, lists, and emphasis.">
            <textarea rows={16} value={form.body} onChange={(e) => update("body", e.target.value)} className="fld font-mono text-xs" />
          </Field>
          <Field label="Primary Scripture" testid="primary_scripture">
            <input value={form.primary_scripture} onChange={(e) => update("primary_scripture", e.target.value)} className="fld" />
          </Field>
          <Field label="Supporting Scriptures" testid="supporting_scriptures" hint="Separate with a semicolon.">
            <input value={form.supporting_scriptures} onChange={(e) => update("supporting_scriptures", e.target.value)} className="fld" />
          </Field>
          <Field label="Context note" testid="context_note">
            <textarea rows={3} value={form.context_note} onChange={(e) => update("context_note", e.target.value)} className="fld" />
          </Field>
          <Field label="What this does not mean" testid="does_not_mean">
            <textarea rows={3} value={form.does_not_mean} onChange={(e) => update("does_not_mean", e.target.value)} className="fld" />
          </Field>
          <Field label="Application" testid="application">
            <textarea rows={3} value={form.application} onChange={(e) => update("application", e.target.value)} className="fld" />
          </Field>
          <Field label="Reflection questions" testid="reflection_questions" hint="One per line.">
            <textarea rows={4} value={form.reflection_questions} onChange={(e) => update("reflection_questions", e.target.value)} className="fld" />
          </Field>
        </div>

        <div className="space-y-4">
          <Field label="Category" testid="category">
            <select value={form.category} onChange={(e) => update("category", e.target.value)} className="fld">
              {["articles", "gospel", "doctrine", "theology", "apologetics", "christian-living", "testimonies", "family", "youth", "children"].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Language" testid="language">
            <input value={form.language} onChange={(e) => update("language", e.target.value)} className="fld" />
          </Field>
          <Field label="Tags" testid="tags" hint="Comma-separated.">
            <input value={form.tags} onChange={(e) => update("tags", e.target.value)} className="fld" />
          </Field>
          <Field label="Reading time (min)" testid="reading_time_min">
            <input type="number" min="1" value={form.reading_time_min} onChange={(e) => update("reading_time_min", e.target.value)} className="fld" />
          </Field>
          <Field label="SEO title" testid="seo_title">
            <input value={form.seo_title} onChange={(e) => update("seo_title", e.target.value)} className="fld" />
          </Field>
          <Field label="SEO description" testid="seo_description">
            <textarea rows={3} value={form.seo_description} onChange={(e) => update("seo_description", e.target.value)} className="fld" />
          </Field>
          <label className="flex items-center gap-2 text-sm text-ink-soft">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => update("featured", e.target.checked)} />
            <span>Featured on homepage</span>
          </label>
          {!isNew && form.slug && (
            <a href={`/articles/${form.slug}`} target="_blank" rel="noopener noreferrer" className="text-sm text-forest hover:underline">
              Preview published version →
            </a>
          )}
        </div>
      </div>

      <style>{`.fld { width: 100%; border: 1px solid hsl(var(--border)); background: hsl(var(--paper)); padding: 0.6rem 0.75rem; font-size: 0.875rem; }`}</style>
    </div>
  );
}

function Field({ label, testid, hint, children }) {
  return (
    <label className="block" data-testid={T.cmsField(testid)}>
      <span className="eyebrow">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-ink-muted mt-1">{hint}</p>}
    </label>
  );
}
